/**
 * PR79: Combined Source Adoption Registry
 * Smart Shikka Core — Kami/Waza/Aiko selective source adoption foundation.
 * Safety: metadata/preview only; no runtime execution.
 */
// ---------------------------------------------------------------------------
// Deep freeze helper
// ---------------------------------------------------------------------------
function deepFreeze(value) {
    if (value === null || typeof value !== "object")
        return value;
    Object.freeze(value);
    for (const key of Object.keys(value)) {
        const child = value[key];
        if (child !== null && typeof child === "object" && !Object.isFrozen(child)) {
            deepFreeze(child);
        }
    }
    return value;
}
// ---------------------------------------------------------------------------
// Shared blocked actions
// ---------------------------------------------------------------------------
const COMMON_BLOCKED_ACTIONS = deepFreeze([
    "run_source_repo_script",
    "install_source_repo_dependencies",
    "execute_workflow",
    "execute_renderer",
    "execute_icon_runtime",
    "network_call",
    "browser_automation",
    "desktop_automation",
    "read_secret",
    "db_write",
    "supabase_call",
    "provider_call",
    "copy_full_repo",
    "vendor_full_repo",
    "copy_forbidden_fonts",
    "copy_forbidden_scripts",
    "copy_pake_source",
    "run_pake_command",
    "auto_save",
    "auto_delete",
    "payment_trigger",
    "role_mutation",
    "secret_mutation",
]);
// ---------------------------------------------------------------------------
// Entry definitions
// ---------------------------------------------------------------------------
const KAMI_ENTRY = deepFreeze({
    id: "kami_document_renderer",
    displayName: "Smart Shikka AI document template renderer, adapted from Kami",
    sourceRepoUrl: "https://github.com/tw93/Kami",
    sourceCommitSha: "594bfb8f2c62f26102a377c2e6cdb8cafa7d61f1",
    sourceLicense: "MIT",
    sourceLicenseVerified: true,
    copiedFileAllowlist: [
        "assets/templates/changelog.html",
        "assets/templates/letter.html",
        "assets/templates/long-doc.html",
        "assets/templates/one-pager.html",
        "assets/templates/portfolio.html",
        "assets/templates/resume.html",
        "assets/templates/slides-weasy.html",
        "plugins/kami/skills/kami/references/design.md",
        "plugins/kami/skills/kami/references/writing.md",
        "plugins/kami/skills/kami/references/production.md",
        "plugins/kami/skills/kami/references/tokens.json",
        "plugins/kami/skills/kami/references/checks_thresholds.json",
    ],
    copiedFileDenylist: [
        "assets/fonts/",
        "assets/demos/",
        "assets/showcase/",
        "dist/",
        ".github/",
        "scripts/",
        "plugins/kami/skills/kami/scripts/",
        "node_modules/",
    ],
    forbiddenSourcePaths: [
        "Kami/assets/fonts/",
        "Kami/assets/demos/",
        "Kami/assets/showcase/",
        "Kami/dist/",
        "Kami/.github/",
        "Kami/scripts/",
        "Kami/plugins/kami/skills/kami/scripts/",
        "Kami/node_modules/",
    ],
    runtimeExecutionAllowed: false,
    networkAllowed: false,
    browserAutomationAllowed: false,
    desktopAutomationAllowed: false,
    fileMutationAllowed: false,
    secretAccessAllowed: false,
    dbAccessAllowed: false,
    providerCallAllowed: false,
    requiresAttribution: true,
    requiresLicenseNotice: true,
    safetyMode: "static_metadata_preview_only",
    blockedActions: COMMON_BLOCKED_ACTIONS,
    notes: [
        "Selected templates and references only. No fonts, demos, showcase, scripts, or build output copied.",
        "Kami fonts are explicitly forbidden.",
        "Pake source is not copied.",
    ],
});
const WAZA_ENTRY = deepFreeze({
    id: "waza_workflow_skills",
    displayName: "Smart Shikka AI workflow skill registry, adapted from Waza",
    sourceRepoUrl: "https://github.com/tw93/Waza",
    sourceCommitSha: "ac1cc9d685718996df081d70e677d527c3ff981f",
    sourceLicense: "MIT",
    sourceLicenseVerified: true,
    copiedFileAllowlist: [
        "skills/think/SKILL.md",
        "skills/check/SKILL.md",
        "skills/hunt/SKILL.md",
        "skills/read/SKILL.md",
        "skills/health/SKILL.md",
        "skills/check/references/persona-catalog.md",
        "skills/check/references/project-context.md",
        "skills/check/references/public-reply.md",
        "skills/hunt/references/failure-patterns.md",
        "skills/hunt/references/ime-unicode.md",
        "skills/hunt/references/logging-techniques.md",
        "skills/hunt/references/rendering-debug.md",
        "skills/read/references/read-methods.md",
    ],
    copiedFileDenylist: [
        "plugins/",
        "tests/",
        "scripts/",
        "skills/*/scripts/",
        ".github/",
        "node_modules/",
        "dist/",
        "fetch.sh",
        "fetch_local.py",
        "fetch_weixin.py",
        "fetch_feishu.py",
    ],
    forbiddenSourcePaths: [
        "Waza/plugins/",
        "Waza/tests/",
        "Waza/scripts/",
        "Waza/skills/*/scripts/",
        "Waza/.github/",
        "Waza/node_modules/",
        "Waza/dist/",
    ],
    runtimeExecutionAllowed: false,
    networkAllowed: false,
    browserAutomationAllowed: false,
    desktopAutomationAllowed: false,
    fileMutationAllowed: false,
    secretAccessAllowed: false,
    dbAccessAllowed: false,
    providerCallAllowed: false,
    requiresAttribution: true,
    requiresLicenseNotice: true,
    safetyMode: "static_metadata_preview_only",
    blockedActions: COMMON_BLOCKED_ACTIONS,
    notes: [
        "Five skill SKILL.md files and selected references only. No scripts, plugins, tests, or build output copied.",
        "fetch.sh, fetch_local.py, fetch_weixin.py, fetch_feishu.py are explicitly forbidden.",
        "Pake source is not copied.",
    ],
});
const AIKO_ENTRY = deepFreeze({
    id: "aiko_icon_system",
    displayName: "Smart Shikka AI icon system, adapted from Aiko",
    sourceRepoUrl: "https://github.com/tw93/aiko",
    sourceCommitSha: "b7ba4c7ddff6416760f0cdf5b53d6cdd65fd1a12",
    sourceLicense: "MIT",
    sourceLicenseVerified: false,
    copiedFileAllowlist: [
        "assets/icons.svg",
    ],
    copiedFileDenylist: [
        "index.html",
        "assets/preview.png",
        "node_modules/",
        "dist/",
        ".git/",
    ],
    forbiddenSourcePaths: [
        "aiko/index.html",
        "aiko/assets/preview.png",
        "aiko/node_modules/",
        "aiko/dist/",
        "aiko/.git/",
    ],
    runtimeExecutionAllowed: false,
    networkAllowed: false,
    browserAutomationAllowed: false,
    desktopAutomationAllowed: false,
    fileMutationAllowed: false,
    secretAccessAllowed: false,
    dbAccessAllowed: false,
    providerCallAllowed: false,
    requiresAttribution: true,
    requiresLicenseNotice: true,
    safetyMode: "static_metadata_preview_only",
    blockedActions: COMMON_BLOCKED_ACTIONS,
    notes: [
        "icons.svg only. index.html, preview.png, full repo dump, .git, node_modules, dist are forbidden.",
        "README states MIT; separate LICENSE file not found in inspected source commit. Use retained attribution and license verification note.",
        "Pake source is not copied.",
    ],
});
// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------
export const SOURCE_ADOPTION_REGISTRY = deepFreeze({
    version: "1.0.0",
    generatedInPr: 79,
    entries: [KAMI_ENTRY, WAZA_ENTRY, AIKO_ENTRY],
});
// ---------------------------------------------------------------------------
// Lookup helpers
// ---------------------------------------------------------------------------
export function getSourceAdoptionEntries() {
    return SOURCE_ADOPTION_REGISTRY.entries;
}
export function getSourceAdoptionEntryById(id) {
    return SOURCE_ADOPTION_REGISTRY.entries.find((e) => e.id === id);
}
//# sourceMappingURL=sourceAdoptionRegistry.js.map